import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getClient360 } from '../../services/api';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, Building2, Mail, Phone, MapPin,
  Receipt, CheckCircle2, Clock, AlertTriangle, Repeat,
  FileText, Truck, Layers, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { StatCard, SurfaceCard } from '../../components/ui/filters-toolkit';

const fmt = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(n || 0);

const STATUS_CLS = {
  draft: 'bg-stone-100 text-stone-700',
  sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-rose-100 text-rose-700',
  cancelled: 'bg-stone-200 text-stone-500',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
  expired: 'bg-amber-100 text-amber-700',
  converted: 'bg-violet-100 text-violet-700',
  delivered: 'bg-emerald-100 text-emerald-700',
};

const TypeIcon = ({ type }) => {
  if (type === 'invoice') return <Receipt className="w-4 h-4 text-[#FF6B4A]" />;
  if (type === 'quote') return <FileText className="w-4 h-4 text-blue-600" />;
  if (type === 'proforma') return <Layers className="w-4 h-4 text-violet-600" />;
  if (type === 'delivery') return <Truck className="w-4 h-4 text-emerald-600" />;
  return <FileText className="w-4 h-4 text-stone-400" />;
};

const TYPE_LABEL = { invoice: 'Factura', quote: 'Presupuesto', proforma: 'Proforma', delivery: 'Albarán' };

const ClientDetail360Page = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getClient360(id);
        if (!cancelled) setData(data);
      } catch {
        toast.error('Error al cargar la ficha 360 del cliente');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }
  if (!data) return null;

  const { client: cli, summary, invoices, quotes, proformas, deliveries, subscriptions, timeline } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/clients')} className="p-2 hover:bg-stone-100 rounded-xl transition-colors" data-testid="client-360-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] flex items-center justify-center shadow-lg shadow-orange-200/40">
              <Building2 className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-stone-900 font-heading">{cli.name}</h1>
              <div className="flex items-center gap-3 mt-1 text-xs text-stone-500 flex-wrap">
                {cli.tax_id && <span className="font-mono">{cli.tax_id}</span>}
                {cli.email && (<span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {cli.email}</span>)}
                {cli.phone && (<span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {cli.phone}</span>)}
                {cli.address && (<span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {cli.address}</span>)}
              </div>
            </div>
          </div>
        </div>
        <Link to={`/clients/${cli.id}/edit`}>
          <Button variant="outline" className="rounded-xl" data-testid="edit-client-from-360">Editar datos del cliente</Button>
        </Link>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="client-360-summary">
        <StatCard
          label="Lifetime value"
          value={fmt(summary.lifetime_value)}
          sub={`${summary.invoices_count} factura${summary.invoices_count === 1 ? '' : 's'} pagadas`}
          icon={CheckCircle2}
          gradient="bg-gradient-to-br from-emerald-50 to-teal-50"
          iconBg="bg-gradient-to-br from-emerald-500 to-teal-500"
          testid="stat-ltv"
        />
        <StatCard
          label="Facturado total"
          value={fmt(summary.invoiced_total)}
          sub={`Pendiente: ${fmt(summary.pending)}`}
          icon={Receipt}
          gradient="bg-gradient-to-br from-orange-50 to-amber-50"
          iconBg="bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24]"
          testid="stat-billed"
        />
        <StatCard
          label="Vencidas"
          value={fmt(summary.overdue)}
          sub="Cobros atrasados"
          icon={AlertTriangle}
          gradient="bg-gradient-to-br from-rose-50 to-red-50"
          iconBg="bg-gradient-to-br from-rose-500 to-red-500"
          testid="stat-overdue"
        />
        <StatCard
          label="MRR · Suscripciones"
          value={fmt(summary.mrr)}
          sub={`${summary.active_subscriptions} activa${summary.active_subscriptions === 1 ? '' : 's'} · ARR ${fmt(summary.arr)}`}
          icon={Repeat}
          gradient="bg-gradient-to-br from-violet-50 to-purple-50"
          iconBg="bg-gradient-to-br from-violet-500 to-purple-500"
          testid="stat-mrr-360"
        />
      </div>

      {/* Two columns: tables + timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Facturas" count={invoices.count} link={`/invoices?client=${encodeURIComponent(cli.name)}`} testid="section-invoices">
            <Table rows={invoices.rows} numberKey="invoice_number" />
          </Section>

          <Section title="Proformas" count={proformas.count} link="/proformas" testid="section-proformas">
            <Table rows={proformas.rows} numberKey="proforma_number" />
          </Section>

          <Section title="Presupuestos" count={quotes.count} link="/quotes" testid="section-quotes">
            <Table rows={quotes.rows} numberKey="quote_number" />
          </Section>

          {deliveries.count > 0 && (
            <Section title="Albaranes" count={deliveries.count} link="/delivery-notes" testid="section-deliveries">
              <Table rows={deliveries.rows} numberKey="delivery_number" />
            </Section>
          )}

          {subscriptions.count > 0 && (
            <Section title="Suscripciones" count={subscriptions.count} link="/subscriptions" testid="section-subs">
              <SurfaceCard>
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-xs uppercase text-stone-500">
                    <tr>
                      <th className="text-left px-4 py-2">Nombre</th>
                      <th className="text-left px-4 py-2">Frec.</th>
                      <th className="text-right px-4 py-2">MRR</th>
                      <th className="text-left px-4 py-2">Próxima</th>
                      <th className="text-center px-4 py-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.rows.map((r) => (
                      <tr key={r.id} className="border-t border-stone-100">
                        <td className="px-4 py-2 font-medium">{r.name || '—'}</td>
                        <td className="px-4 py-2">{r.frequency_type}</td>
                        <td className="px-4 py-2 text-right font-mono">{fmt(r.mrr)}</td>
                        <td className="px-4 py-2">{r.next_billing_date || '—'}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_CLS[r.status] || 'bg-stone-100 text-stone-700'}`}>{r.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SurfaceCard>
            </Section>
          )}
        </div>

        {/* Timeline */}
        <div className="space-y-3" data-testid="timeline-section">
          <h2 className="text-[10px] font-bold uppercase tracking-widest text-stone-400">Actividad reciente</h2>
          {timeline.length === 0 ? (
            <SurfaceCard className="!p-6 text-center text-sm text-stone-400">Sin actividad aún.</SurfaceCard>
          ) : (
            <div className="space-y-2">
              {timeline.map((e, i) => (
                <SurfaceCard key={`${e.type}-${e.id}-${i}`} className="!p-3 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-stone-50 flex items-center justify-center flex-shrink-0">
                      <TypeIcon type={e.type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-stone-700">{TYPE_LABEL[e.type] || e.type} · <span className="font-mono">{e.ref || '—'}</span></p>
                      <p className="text-[11px] text-stone-400">{e.date}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm font-semibold text-stone-900">{fmt(e.amount)}</p>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_CLS[e.status] || 'bg-stone-100 text-stone-600'}`}>{e.status}</span>
                    </div>
                  </div>
                </SurfaceCard>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, count, link, children, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-[10px] font-bold uppercase tracking-widest text-stone-400">
        {title} <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded-full text-[10px] font-bold">{count}</span>
      </h2>
      {link && count > 0 && (
        <Link to={link} className="text-xs font-semibold text-[#FF6B4A] hover:text-[#E05A3E] flex items-center gap-0.5">
          Ver todas <ChevronRight className="w-3 h-3" />
        </Link>
      )}
    </div>
    {count === 0 ? (
      <SurfaceCard className="!p-6 text-center text-sm text-stone-400">Sin {title.toLowerCase()}.</SurfaceCard>
    ) : (
      children
    )}
  </div>
);

const Table = ({ rows, numberKey }) => (
  <SurfaceCard>
    <table className="w-full text-sm">
      <thead className="bg-stone-50 text-xs uppercase text-stone-500">
        <tr>
          <th className="text-left px-4 py-2">Número</th>
          <th className="text-left px-4 py-2">Fecha</th>
          <th className="text-center px-4 py-2">Estado</th>
          <th className="text-right px-4 py-2">Total</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-stone-100 hover:bg-orange-50/30 transition-colors">
            <td className="px-4 py-2 font-mono text-[#FF6B4A] text-xs">{r[numberKey] || 'Borrador'}</td>
            <td className="px-4 py-2 text-stone-500 text-xs">{r.issue_date}</td>
            <td className="px-4 py-2 text-center">
              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_CLS[r.status] || 'bg-stone-100 text-stone-700'}`}>{r.status}</span>
            </td>
            <td className="px-4 py-2 text-right font-mono font-medium">{fmt(r.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </SurfaceCard>
);

export default ClientDetail360Page;
